import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
	ArrowLeft,
	FlaskConical,
	Loader2,
	RotateCcw,
	Star,
	TriangleAlert,
} from 'lucide-react';
import { PageTitle } from '@/components/ui';
import { useGetMatchForm } from '@/services';
import { useSimulateMatch } from '@/services/mutations/match';
import {
	MATCH_QUESTION_CODE_LABELS,
	getApproach,
	getFocusTopic,
	getGenderLabel,
	getHighlightTag,
} from '@/constants/professionalTags';
import { getActiveQuestions } from '@/utils/formQuestionShowCondition';
import {
	MATCH_MAX_MULTIPLE_SELECTIONS,
	joinMultipleAnswer,
	splitMultipleAnswer,
} from '@/pages/public/match/matchFlow';
import { cn } from '@/lib/utils';
import { MATCH_SIMULATOR_PERSONAS } from './matchSimulatorPersonas';

type Answers = Record<string, string | null>;

/** Rótulos das dimensões do modelo (backend `match-scoring.ts`). */
const DIMENSION_LABELS: Record<string, string> = {
	topics: 'Temas',
	style: 'Abordagem (estilo)',
	priority: 'Tag de destaque',
	gender: 'Gênero',
	availability: 'Agenda',
	audience: 'Público',
	age: 'Faixa etária',
	experience: 'Primeira terapia',
};

const STYLE_LABELS: Record<string, string> = {
	PRACTICAL: 'Prático',
	REFLECTIVE: 'Reflexivo',
	PRESENT: 'Presente/escuta',
	RELATIONAL: 'Relacional',
};

const PERIOD_LABELS: Record<string, string> = {
	MORNING: 'Manhã',
	AFTERNOON: 'Tarde',
	EVENING: 'Noite',
};

const AGE_LABELS: Record<string, string> = {
	YOUNG: 'Jovem',
	ADULT: 'Adulto',
	SENIOR: 'Terceira idade',
};

const AUDIENCE_LABELS: Record<string, string> = {
	COUPLE_FAMILY: 'Casal/família',
};

const chipClass =
	'inline-flex items-center gap-1 rounded-full bg-stone-100 px-2.5 py-1 text-[11px] font-bold text-stone-600';

/** Preferências extraídas → chips legíveis, na ordem de peso do modelo. */
function describePreferences(prefs: Record<string, unknown>): string[] {
	const chips: string[] = [];
	const topics = prefs.topics as string[] | undefined;
	if (topics?.length) {
		chips.push(
			`Temas: ${topics.map((t) => getFocusTopic(t)?.label ?? t).join(', ')}`,
		);
	}
	if (prefs.style) {
		const style = String(prefs.style);
		chips.push(`Estilo: ${STYLE_LABELS[style] ?? style}`);
	}
	if (prefs.gender) {
		chips.push(`Gênero: ${getGenderLabel(String(prefs.gender))}`);
	}
	if (prefs.priority) {
		const priority = String(prefs.priority);
		chips.push(
			`Destaque: ${getHighlightTag(priority)?.label ?? priority}`,
		);
	}
	if (prefs.period) {
		const period = String(prefs.period);
		chips.push(`Agenda: ${PERIOD_LABELS[period] ?? period}`);
	}
	if (prefs.audience) {
		const audience = String(prefs.audience);
		chips.push(`Público: ${AUDIENCE_LABELS[audience] ?? audience}`);
	}
	if (prefs.age_group) {
		const age = String(prefs.age_group);
		chips.push(`Idade: ${AGE_LABELS[age] ?? age}`);
	}
	if (prefs.first_therapy) chips.push('Primeira terapia');
	return chips;
}

/**
 * Admin › Match › Simulador: responde o questionário numa tela só e mostra o
 * ranking COMPLETO dos psicólogos, com a nota e a conta por dimensão. Nada é
 * salvo (`POST /match/simulate`), então dá para testar quantas personas quiser
 * sem criar conta nem gastar a resposta única de um usuário.
 */
const MatchSimulatorPage = () => {
	const { data: form, isLoading, isError } = useGetMatchForm();
	const { mutate: simulate, data: simulation, isPending } = useSimulateMatch();
	const [answers, setAnswers] = useState<Answers>({});
	const [personaKey, setPersonaKey] = useState<string | null>(null);
	const [openBreakdownId, setOpenBreakdownId] = useState<string | null>(null);

	const questions = useMemo(() => form?.form_question ?? [], [form]);
	/** Respeita show_condition: só entra o que o usuário realmente veria. */
	const activeQuestions = useMemo(
		() => getActiveQuestions(questions, answers),
		[questions, answers],
	);

	/** Respostas de perguntas que deixaram de ser exibidas não vão para o cálculo. */
	const payload = useMemo(() => {
		const visible: Answers = {};
		for (const question of activeQuestions) {
			const value = answers[String(question.number)];
			if (value) visible[String(question.number)] = value;
		}
		return visible;
	}, [activeQuestions, answers]);

	// Simula sozinho a cada mudança: trocar uma alternativa e ver o ranking
	// mexer na hora é o ponto do simulador.
	useEffect(() => {
		if (!form) return;
		const timer = window.setTimeout(
			() => simulate({ answer: payload }),
			300,
		);
		return () => window.clearTimeout(timer);
	}, [form, payload, simulate]);

	const applyPersona = (key: string) => {
		const persona = MATCH_SIMULATOR_PERSONAS.find((p) => p.key === key);
		if (!persona || !form) return;
		const next: Answers = {};
		for (const question of form.form_question) {
			const value = question.code
				? persona.answersByCode[question.code]
				: undefined;
			if (value) next[String(question.number)] = value;
		}
		setAnswers(next);
		setPersonaKey(key);
	};

	const clearAnswers = () => {
		setAnswers({});
		setPersonaKey(null);
	};

	const toggleOption = (question: Forms.FormQuestion, optionKey: string) => {
		setPersonaKey(null);
		setAnswers((prev) => {
			const field = String(question.number);
			if (question.type === 'MULTIPLE_CHOICE') {
				const selected = splitMultipleAnswer(prev[field]);
				const next = selected.includes(optionKey)
					? selected.filter((k) => k !== optionKey)
					: selected.length >= MATCH_MAX_MULTIPLE_SELECTIONS
						? selected
						: [...selected, optionKey];
				return {
					...prev,
					[field]: joinMultipleAnswer(question, next) || null,
				};
			}
			return {
				...prev,
				[field]: prev[field] === optionKey ? null : optionKey,
			};
		});
	};

	const answeredCount = Object.values(payload).filter(Boolean).length;
	const activePersona = MATCH_SIMULATOR_PERSONAS.find(
		(p) => p.key === personaKey,
	);

	if (isLoading) {
		return (
			<section className="px-8 py-8">
				<p className="text-sm text-stone-400">
					Carregando o questionário do match...
				</p>
			</section>
		);
	}

	if (isError || !form) {
		return (
			<section className="px-8 py-8">
				<div className="rounded-[2rem] border border-dashed border-stone-200 bg-stone-50/50 p-10 text-center">
					<h3 className="text-base font-black text-stone-900">
						Nenhum formulário de match ativo
					</h3>
					<p className="mx-auto mt-2 max-w-md text-sm text-stone-400">
						Ative ou crie o formulário em Match para poder simular.
					</p>
					<Link
						to="/admin/match"
						className="mt-6 inline-flex items-center gap-2 rounded-full bg-stone-900 px-6 py-3 text-sm font-bold text-white"
					>
						Ir para Match
					</Link>
				</div>
			</section>
		);
	}

	return (
		<section className="space-y-6 px-8 py-8">
			<div className="flex flex-wrap items-start justify-between gap-4">
				<div className="space-y-1">
					<div className="flex items-center gap-3">
						<span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-brand-pink text-white">
							<FlaskConical className="h-5 w-5" />
						</span>
						<PageTitle text="Simulador do match" />
					</div>
					<p className="max-w-2xl text-sm text-stone-400">
						Responda como um usuário responderia e veja o ranking
						completo dos psicólogos, com a nota e o porquê de cada
						um. <strong>Nada é salvo</strong>: nenhuma resposta é
						gravada e nenhum usuário é afetado.
					</p>
				</div>
				<Link
					to="/admin/match"
					className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white px-4 py-2 text-xs font-bold text-stone-700 transition-colors hover:border-stone-900 hover:text-stone-900"
				>
					<ArrowLeft className="h-3.5 w-3.5" />
					Voltar para o Match
				</Link>
			</div>

			<div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
				{/* Coluna esquerda: as perguntas */}
				<div className="space-y-4">
					<div className="rounded-2xl border border-stone-100 bg-white p-4 shadow-sm">
						<p className="text-[11px] font-black uppercase tracking-wide text-stone-400">
							Personas prontas
						</p>
						<div className="mt-3 flex flex-wrap gap-2">
							{MATCH_SIMULATOR_PERSONAS.map((persona) => (
								<button
									key={persona.key}
									type="button"
									onClick={() => applyPersona(persona.key)}
									title={persona.description}
									className={cn(
										'rounded-full px-3 py-1.5 text-xs font-bold transition-colors',
										personaKey === persona.key
											? 'bg-stone-900 text-white'
											: 'bg-stone-100 text-stone-600 hover:bg-stone-200',
									)}
								>
									{persona.label}
								</button>
							))}
							<button
								type="button"
								onClick={clearAnswers}
								className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 px-3 py-1.5 text-xs font-bold text-stone-500 transition-colors hover:border-stone-900 hover:text-stone-900"
							>
								<RotateCcw className="h-3 w-3" />
								Limpar
							</button>
						</div>
						{activePersona ? (
							<p className="mt-3 text-xs text-stone-400">
								{activePersona.description}
							</p>
						) : null}
					</div>

					{activeQuestions.map((question) => {
						const field = String(question.number);
						const selected =
							question.type === 'MULTIPLE_CHOICE'
								? splitMultipleAnswer(answers[field])
								: answers[field]
									? [String(answers[field])]
									: [];
						return (
							<div
								key={question.id}
								className="rounded-2xl border border-stone-100 bg-white p-4 shadow-sm"
							>
								<div className="flex flex-wrap items-center gap-2">
									<span className="text-xs font-black text-stone-400">
										{question.number}.
									</span>
									<h3 className="text-sm font-bold text-stone-900">
										{question.title}
									</h3>
									{question.code ? (
										<span className="rounded-full bg-brand-pink/10 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-brand-pink">
											{MATCH_QUESTION_CODE_LABELS[
												question.code
											] ?? question.code}
										</span>
									) : (
										<span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-stone-400">
											não pontua
										</span>
									)}
								</div>
								<div className="mt-3 flex flex-wrap gap-2">
									{(question.options ?? []).map((option) => {
										const isOn = selected.includes(
											option.key,
										);
										return (
											<button
												key={option.key}
												type="button"
												onClick={() =>
													toggleOption(
														question,
														option.key,
													)
												}
												className={cn(
													'rounded-xl border px-3 py-2 text-left text-xs font-semibold transition-colors',
													isOn
														? 'border-stone-900 bg-stone-900 text-white'
														: 'border-stone-200 text-stone-600 hover:border-stone-400',
												)}
											>
												<span className="mr-1 opacity-60">
													{option.key}
												</span>
												{option.label}
											</button>
										);
									})}
								</div>
							</div>
						);
					})}
				</div>

				{/* Coluna direita: o ranking */}
				<div className="space-y-4 xl:sticky xl:top-6 xl:self-start">
					<div className="rounded-2xl border border-stone-100 bg-white p-4 shadow-sm">
						<div className="flex flex-wrap items-center justify-between gap-3">
							<p className="text-[11px] font-black uppercase tracking-wide text-stone-400">
								Resultado do modelo
							</p>
							{isPending ? (
								<span className="inline-flex items-center gap-1.5 text-xs font-bold text-stone-400">
									<Loader2 className="h-3.5 w-3.5 animate-spin" />
									calculando
								</span>
							) : null}
						</div>

						<div className="mt-3 flex flex-wrap gap-2">
							<span className={chipClass}>
								{answeredCount} de {activeQuestions.length}{' '}
								respondidas
							</span>
							<span className={chipClass}>
								confiança{' '}
								{simulation
									? `${Math.round(simulation.confidence * 100)}%`
									: '—'}
							</span>
							<span className={chipClass}>
								{simulation?.candidate_count ?? 0} psicólogos
								elegíveis
							</span>
						</div>

						{simulation?.fallback ? (
							<div className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
								<TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
								<span>
									{simulation.fallback_reason ===
									'LOW_CONFIDENCE'
										? 'Fallback: respostas neutras demais (confiança abaixo do mínimo). O usuário veria a ordem do admin, sem percentual.'
										: 'Fallback: nenhum psicólogo tem perfil preenchido (abordagem, temas ou tag). O usuário veria a ordem do admin.'}
								</span>
							</div>
						) : null}

						{simulation &&
						describePreferences(simulation.preferences).length >
							0 ? (
							<div className="mt-3">
								<p className="text-[11px] font-black uppercase tracking-wide text-stone-400">
									O que o modelo entendeu
								</p>
								<div className="mt-2 flex flex-wrap gap-2">
									{describePreferences(
										simulation.preferences,
									).map((chip) => (
										<span key={chip} className={chipClass}>
											{chip}
										</span>
									))}
								</div>
							</div>
						) : null}
					</div>

					<div className="space-y-3">
						{(simulation?.professionals ?? []).map(
							(professional) => {
								const isOpen =
									openBreakdownId === professional.id;
								const subtitle = [
									getApproach(professional.main_approach)
										?.label,
									getHighlightTag(professional.highlight_tag)
										?.label,
									getGenderLabel(professional.gender),
								]
									.filter(Boolean)
									.join(' · ');
								return (
									<div
										key={professional.id}
										className={cn(
											'rounded-2xl border bg-white p-4 shadow-sm',
											professional.in_result
												? 'border-brand-orange/60'
												: 'border-stone-100',
										)}
									>
										<div className="flex items-start justify-between gap-3">
											<div className="min-w-0">
												<div className="flex flex-wrap items-center gap-2">
													<span className="text-xs font-black text-stone-400">
														{professional.rank}º
													</span>
													<h3 className="truncate text-sm font-bold text-stone-900">
														{
															professional.first_name
														}{' '}
														{professional.last_name}
													</h3>
													{professional.in_result ? (
														<span className="inline-flex items-center gap-1 rounded-full bg-brand-orange/10 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-brand-orange">
															<Star className="h-3 w-3" />
															aparece pro usuário
														</span>
													) : null}
													{!professional.matchable ? (
														<span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-stone-400">
															perfil vazio
														</span>
													) : null}
													{professional.gender_penalty ? (
														<span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-rose-600">
															gênero ≠ pedido
														</span>
													) : null}
												</div>
												<p className="mt-1 truncate text-xs text-stone-400">
													{subtitle}
												</p>
											</div>
											<div className="shrink-0 text-right">
												<span className="text-lg font-black text-stone-900">
													{professional.match_score}%
												</span>
												<p className="text-[10px] font-bold uppercase text-stone-400">
													afinidade
												</p>
											</div>
										</div>

										<div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-stone-100">
											<div
												className={cn(
													'h-full rounded-full',
													professional.in_result
														? 'bg-brand-orange'
														: 'bg-stone-300',
												)}
												style={{
													width: `${professional.match_score}%`,
												}}
											/>
										</div>

										{professional.match_reasons.length >
										0 ? (
											<ul className="mt-3 space-y-1">
												{professional.match_reasons.map(
													(reason) => (
														<li
															key={reason}
															className="text-xs text-stone-500"
														>
															• {reason}
														</li>
													),
												)}
											</ul>
										) : null}

										<button
											type="button"
											onClick={() =>
												setOpenBreakdownId(
													isOpen
														? null
														: professional.id,
												)
											}
											className="mt-3 text-[11px] font-black uppercase tracking-wide text-stone-400 transition-colors hover:text-stone-900"
										>
											{isOpen
												? 'ocultar a conta'
												: 'ver a conta por dimensão'}
										</button>

										{isOpen ? (
											<table className="mt-2 w-full text-left text-xs">
												<thead>
													<tr className="text-[10px] uppercase text-stone-400">
														<th className="py-1 font-black">
															Dimensão
														</th>
														<th className="py-1 font-black">
															Peso
														</th>
														<th className="py-1 font-black">
															Afinidade
														</th>
													</tr>
												</thead>
												<tbody>
													{professional.breakdown.map(
														(row) => (
															<tr
																key={
																	row.dimension
																}
																className="border-t border-stone-100"
															>
																<td className="py-1 font-semibold text-stone-600">
																	{DIMENSION_LABELS[
																		row
																			.dimension
																	] ??
																		row.dimension}
																	{!row.informed ? (
																		<span className="ml-1 font-normal text-stone-400">
																			(não
																			informado)
																		</span>
																	) : null}
																</td>
																<td className="py-1 text-stone-500">
																	{row.weight.toFixed(
																		2,
																	)}
																</td>
																<td className="py-1 text-stone-500">
																	{Math.round(
																		row.similarity *
																			100,
																	)}
																	%
																</td>
															</tr>
														),
													)}
													{professional.breakdown
														.length === 0 ? (
														<tr>
															<td
																colSpan={3}
																className="py-2 text-stone-400"
															>
																Nenhuma dimensão
																contada: as
																respostas não
																trouxeram
																preferência
																válida.
															</td>
														</tr>
													) : null}
												</tbody>
											</table>
										) : null}
									</div>
								);
							},
						)}

						{simulation && simulation.professionals.length === 0 ? (
							<div className="rounded-2xl border border-dashed border-stone-200 bg-stone-50/50 p-6 text-center text-sm text-stone-400">
								Nenhum psicólogo elegível: o match só considera
								profissionais autorizados, visíveis e com
								serviço de psicologia.
							</div>
						) : null}
					</div>
				</div>
			</div>
		</section>
	);
};

export { MatchSimulatorPage };
